import React, { useEffect, useState, useCallback, useRef } from "react";
import SEO from "../SEO/SEO";
import { getBackendBaseUrl } from "../../config/backendUrl";
import "./Journey.css";

const JOURNEY_API_URL = `${getBackendBaseUrl()}/journey_api.php`;

/* Stable, static SEO for /journey. Rendered on every state (loading,
 * error and ready) so the page always exposes a real title, description
 * and canonical to crawlers - independent of the backend response. */
const JOURNEY_SEO = {
  title: "Our Journey | Boldly English",
  description:
    "Explore the journey of Boldly English, including college visits, student interactions, workshops, seminars, partnerships, and real-world English learning experiences.",
  canonical: "https://boldlyenglish.com/journey",
};

/* ------------------------------------------------------------------ */

function useScrollReveal(deps) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".jn-reveal:not(.visible)").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

function initials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

/* Accept a bare YouTube id or any common YouTube URL and return the id */
function youtubeId(src = "") {
  if (!src) return "";
  if (!src.includes("/") && !src.includes(".")) return src.trim();
  const m = src.match(
    /(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{6,})/
  );
  return m ? m[1] : src.trim();
}

/* ------------------------------------------------------------------ *
 *  Slider: a fixed-height, swipeable/arrow-navigable carousel.
 *  No matter how many items the admin adds, the section's on-screen
 *  height never grows - visitors swipe or click through pages instead.
 * ------------------------------------------------------------------ */
function useCardsPerView({ desktop, tablet, mobile }) {
  const [n, setN] = useState(desktop);
  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      if (w <= 640) setN(mobile);
      else if (w <= 992) setN(tablet);
      else setN(desktop);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [desktop, tablet, mobile]);
  return n;
}

function Slider({ items, renderItem, getKey, perView = { desktop: 3, tablet: 2, mobile: 1 }, className = "" }) {
  const cardsToShow = useCardsPerView(perView);
  const shown = Math.max(Math.min(cardsToShow, items.length), 1);
  const [index, setIndex] = useState(0);
  const touchX = useRef(null);

  useEffect(() => {
    setIndex((i) => Math.min(i, Math.max(items.length - shown, 0)));
  }, [items.length, shown]);

  const canSlide = items.length > shown;
  const maxIndex = Math.max(items.length - shown, 0);

  const prev = () => setIndex((i) => (i === 0 ? maxIndex : i - 1));
  const next = () => setIndex((i) => (i >= maxIndex ? 0 : i + 1));

  const visible = [];
  for (let i = 0; i < shown; i++) {
    const originalIndex = (index + i) % items.length;
    visible.push({ item: items[originalIndex], originalIndex });
  }

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 40) (dx > 0 ? prev : next)();
  };

  return (
    <div className={`jn-slider jn-reveal ${className}`}>
      <div
        className="jn-slider-track"
        style={{ "--jn-cards": shown }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {visible.map(({ item, originalIndex }, i) => (
          <div className="jn-slide" key={getKey ? getKey(item) : i}>
            {renderItem(item, originalIndex)}
          </div>
        ))}
      </div>

      {canSlide && (
        <div className="jn-slider-controls">
          <button type="button" className="jn-arrow" onClick={prev} aria-label="Previous">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="jn-dots">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                type="button"
                className={`jn-dot ${i === index ? "active" : ""}`}
                onClick={() => setIndex(i)}
                aria-label={`Go to page ${i + 1}`}
              />
            ))}
          </div>
          <button type="button" className="jn-arrow" onClick={next} aria-label="Next">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

/* Small inline icons - keeps the page dependency-free */
const PinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 22s7-7.05 7-12a7 7 0 1 0-14 0c0 4.95 7 12 7 12Z" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="12" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const SealIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
    <path d="M9 9.3l1.8 1.8L15 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8.5 14.2 7 21l5-2.3 5 2.3-1.5-6.8" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

const QuoteIcon = () => (
  <svg width="30" height="24" viewBox="0 0 32 24" fill="none" aria-hidden="true">
    <path d="M4 12.5C4 6.7 8 3 13.5 2v3.4c-3 .9-4.7 3-4.9 5.6H13v9H2v-6.4Zm16 0C20 6.7 24 3 29.5 2v3.4c-3 .9-4.7 3-4.9 5.6H29v9H18v-6.4Z" fill="currentColor" />
  </svg>
);

/* ------------------------------------------------------------------ */

function ReviewVideo({ video }) {
  const [play, setPlay] = useState(false);

  let frame;
  if (video.type === "drive") {
    frame = (
      <iframe
        src={`https://drive.google.com/file/d/${video.src}/preview`}
        title={video.title}
        allow="autoplay"
        allowFullScreen
      />
    );
  } else if (video.type === "youtube") {
    const id = youtubeId(video.src);
    const thumb = video.poster || `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    frame = play ? (
      <iframe
        src={`https://www.youtube.com/embed/${id}?autoplay=1`}
        title={video.title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    ) : (
      <button
        type="button"
        className="jn-video-thumb"
        style={{ backgroundImage: `url(${thumb})` }}
        onClick={() => setPlay(true)}
        aria-label={`Play ${video.title}`}
      >
        <span className="jn-play" />
      </button>
    );
  } else if (video.src) {
    frame = <video src={video.src} controls poster={video.poster || undefined} />;
  } else {
    frame = <div className="jn-video-missing">Video unavailable</div>;
  }

  return (
    <div className="jn-video-card">
      <span className="jn-video-quote"><QuoteIcon /></span>
      <div className="jn-video-frame">{frame}</div>
      <div className="jn-video-info">
        <h4>{video.title}</h4>
        <p>{video.college}</p>
      </div>
    </div>
  );
}

export default function Journey() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const res = await fetch(JOURNEY_API_URL);
      const json = await res.json();
      if (json && json.success) {
        setData(json);
        setStatus("ready");
      } else {
        setStatus("error");
      }
    } catch (e) {
      console.error("Failed to load journey content:", e);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useScrollReveal([status]);

  const s = (data && data.settings) || {};
  const stats = (data && data.stats) || [];
  const colleges = (data && data.colleges) || [];
  const videos = (data && data.videos) || [];
  const mous = (data && data.mous) || [];
  const timeline = (data && data.timeline) || [];

  const ready = status === "ready";

  /* One page, always rendered. The hero (H1 + descriptive copy) is fully
   * static and API-independent: it is the first paint, it is what the
   * build-time prerender captures, and it never gets torn down or swapped -
   * so there is no "loading" screen and no layout shift when the backend
   * responds. The API only ADDS content below the copy (stats + the four
   * carousels + CTA) once it has loaded, fading it in in place.
   *
   * Any admin-configured hero eyebrow / subtitle enhance the same hero
   * in place after load; the H1 stays "Our Journey" (the canonical,
   * prerendered heading this page is indexed on). */
  return (
    <div className="jn-page">
      <SEO
        title={JOURNEY_SEO.title}
        description={JOURNEY_SEO.description}
        canonical={JOURNEY_SEO.canonical}
      />

      {/* Hero - static, indexable, present on every state */}
      <section className="jn-hero">
        <div className="jn-hero-inner">
          <h1>Our Journey</h1>
          <div className="jn-hero-copy">
  <p className="jn-hero-sub">
    Discover how Boldly English connects with students through college
    workshops, seminars, speaking practice, and long-term campus partnerships.
  </p>

  {ready && s.hero_subtitle && (
    <p className="jn-hero-sub jn-enter">
      {s.hero_subtitle}
    </p>
  )}
</div>

          {ready && stats.length > 0 && (
            <div className="jn-stats jn-enter">
              {stats.map((st) => (
                <div className="jn-stat" key={st.id}>
                  <span className="jn-stat-value">{st.value}</span>
                  <span className="jn-stat-label">{st.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Non-intrusive status line. Empty on loading/ready; a short
              note + retry on failure. suppressHydrationWarning because the
              prerendered snapshot (API blocked -> error) and the live first
              paint (loading) can legitimately differ here. */}
          <div className="jn-hero-status" suppressHydrationWarning>
            {status === "error" && (
              <p className="jn-hero-status-msg">
                Live highlights are refreshing.{" "}
                <button type="button" className="jn-retry jn-retry--inline" onClick={load}>
                  Retry
                </button>
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Colleges visited */}
      {colleges.length > 0 && (
        <section className="jn-section">
          <div className="jn-container">
            <h2 className="jn-reveal">{s.colleges_heading || "Colleges We've Visited"}</h2>
            {s.colleges_lead && <p className="jn-section-lead jn-reveal">{s.colleges_lead}</p>}
            <Slider
              items={colleges}
              getKey={(c) => c.id}
              renderItem={(c) => (
                <div className="jn-college-card">
                  <div className="jn-college-photo">
                    {c.logo ? (
                      <img src={c.logo} alt={c.name} />
                    ) : (
                      <span className="jn-college-badge">{initials(c.name)}</span>
                    )}
                    {c.date && <span className="jn-college-stamp">{c.date}</span>}
                  </div>
                  <div className="jn-college-body">
                    <h3>{c.name}</h3>
                    {c.location && (
                      <p className="jn-college-loc">
                        <PinIcon /> {c.location}
                      </p>
                    )}
                    {c.note && <p className="jn-college-note">{c.note}</p>}
                  </div>
                </div>
              )}
            />
          </div>
        </section>
      )}

      {/* Student review videos */}
      {videos.length > 0 && (
        <section className="jn-section jn-section-alt">
          <div className="jn-container">
            <h2 className="jn-reveal">{s.videos_heading || "Students, In Their Own Words"}</h2>
            {s.videos_lead && <p className="jn-section-lead jn-reveal">{s.videos_lead}</p>}
            <Slider
              items={videos}
              getKey={(v) => v.id}
              renderItem={(v) => <ReviewVideo video={v} />}
            />
          </div>
        </section>
      )}

      {/* MOUs */}
      {mous.length > 0 && (
        <section className="jn-section">
          <div className="jn-container">
            <h2 className="jn-reveal">{s.mous_heading || "MOUs & Partnerships"}</h2>
            {s.mous_lead && <p className="jn-section-lead jn-reveal">{s.mous_lead}</p>}
            <Slider
              items={mous}
              getKey={(m) => m.id}
              renderItem={(m) => (
                <div className="jn-mou-card">
                  <div className="jn-mou-topline">
                    <span className="jn-mou-kicker">Memorandum of Understanding</span>
                    <span className="jn-mou-seal"><SealIcon /></span>
                  </div>
                  <h3>{m.partner}</h3>
                  {m.scope && <p className="jn-mou-scope">{m.scope}</p>}
                  {m.points && m.points.length > 0 && (
                    <ul className="jn-mou-points">
                      {m.points.map((p, i) => (
                        <li key={i}>{p}</li>
                      ))}
                    </ul>
                  )}
                  {m.date && (
                    <div className="jn-mou-signed">
                      <span>Signed</span>
                      <strong>{m.date}</strong>
                    </div>
                  )}
                </div>
              )}
            />
          </div>
        </section>
      )}

      {/* Timeline / story */}
      {timeline.length > 0 && (
        <section className="jn-section jn-section-alt">
          <div className="jn-container">
            <h2 className="jn-reveal">{s.timeline_heading || "The Story So Far"}</h2>
            <Slider
              items={timeline}
              getKey={(t) => t.id}
              className="jn-milestone-slider"
              renderItem={(t, i) => {
                const isLast = i === timeline.length - 1;
                return (
                  <div className={`jn-milestone ${isLast ? "is-current" : ""}`}>
                    <div className="jn-milestone-rail">
                      <span className="jn-milestone-dot">
                        {isLast && <span className="jn-milestone-ping" />}
                      </span>
                    </div>
                    {t.period && <span className="jn-milestone-period">{t.period}</span>}
                    <div className="jn-milestone-card">
                      <h3>{t.title}</h3>
                      {t.text && <p>{t.text}</p>}
                    </div>
                  </div>
                );
              }}
            />
          </div>
        </section>
      )}

      {/* CTA */}
      {(s.cta_heading || s.cta_text) && (
        <section className="jn-cta">
          <div className="jn-container jn-cta-inner jn-reveal">
            {s.cta_heading && <h2>{s.cta_heading}</h2>}
            {s.cta_text && <p>{s.cta_text}</p>}
            {s.cta_button_label && (
              <a className="jn-cta-btn" href={s.cta_button_link || "/about"}>
                {s.cta_button_label}
              </a>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
